import type { EChartsOption, XAXisComponentOption, YAXisComponentOption } from 'echarts';

import type { Theme } from '../theme/readTheme';
import { cell, shortDate, truncate } from './format';
import type { Frame } from './frame';

/**
 * The house style, expressed once.
 *
 * Every cartesian chart in the app is assembled from these builders rather than
 * from a hand-written option object, which is what stops one chart drifting
 * loud while its neighbour stays quiet. The specs here are fixed:
 *
 *   bars    ≤ 24px thick, 4px rounded data-end, square at the baseline
 *   lines   2px, round join and cap
 *   markers ≥ 8px, with a 2px ring in the surface colour where they overlap
 *   areas   the series hue at 10% opacity — a wash, never a block
 *   grid    hairline, solid, one step off the surface; never dashed
 *
 * Text never wears a series colour. Identity comes from the coloured mark
 * beside the label — a swatch, a line key, the bar itself.
 */

export const BAR_MAX_WIDTH = 24;
export const MARKER_SIZE = 8;
export const AREA_OPACITY = 0.1;
export const SURFACE_GAP = 2;

const LABEL_FONT_SIZE = 11;

const axisTextStyle = (theme: Theme) => ({
  color: theme.inkMuted,
  fontSize: LABEL_FONT_SIZE,
  fontFamily: theme.font,
});

/** Padding around the plot. `containLabel` keeps axis text inside the box, so a
 *  card's fixed height never crops the x-axis band into a nested scrollbar. */
export const baseGrid = (opts: { legend: boolean; rotatedLabels?: boolean }) => ({
  left: 4,
  // Enough room that the last axis tick isn't clipped by the plot edge.
  right: 16,
  top: 12,
  bottom: (opts.legend ? 28 : 4) + (opts.rotatedLabels ? 16 : 0),
  containLabel: true,
});

/*
 * Axis builders come in X and Y pairs over one shared spec.
 *
 * A bar chart and a column chart are the same chart rotated: one puts the
 * categories on Y and the measure on X, the other the reverse. Sharing the spec
 * and splitting only the type keeps the two orientations from drifting apart —
 * and keeps ECharts' separate X/Y option types happy without casts.
 */

const categorySpec = (
  theme: Theme,
  frame: Frame,
  opts: { rotate?: boolean; maxLabel?: number } = {},
) => ({
  type: 'category' as const,
  data: frame.categories,
  axisLine: { lineStyle: { color: theme.axis, width: 1 } },
  axisTick: { show: false },
  splitLine: { show: false },
  axisLabel: {
    ...axisTextStyle(theme),
    hideOverlap: true,
    rotate: opts.rotate ? 35 : 0,
    formatter: (value: string) =>
      truncate(frame.temporal ? shortDate(value) : value, opts.maxLabel ?? 18),
  },
});

const valueSpec = (theme: Theme, opts: { name?: string } = {}) => ({
  type: 'value' as const,
  name: opts.name || undefined,
  nameTextStyle: { ...axisTextStyle(theme), align: 'left' as const, padding: [0, 0, 4, 0] },
  nameGap: 12,
  axisLine: { show: false },
  axisTick: { show: false },
  // Gridlines carry the values that aren't directly labelled, so they stay —
  // but recessive: one step off the surface, hairline, solid.
  splitLine: { show: true, lineStyle: { color: theme.grid, width: 1, type: 'solid' as const } },
  axisLabel: { ...axisTextStyle(theme), formatter: (v: number) => cell(v) },
});

/** Categories along the bottom — columns, trends. */
export const categoryAxisX = (
  theme: Theme,
  frame: Frame,
  opts: { rotate?: boolean; maxLabel?: number } = {},
): XAXisComponentOption => categorySpec(theme, frame, opts);

/** Categories down the left — horizontal bars and leaderboard-style charts,
 *  where a long name gets a whole gutter instead of a 35-degree rotation. */
export const categoryAxisY = (
  theme: Theme,
  frame: Frame,
  maxLabel = 22,
): YAXisComponentOption => ({
  ...categorySpec(theme, frame, { maxLabel }),
  inverse: true,
  axisLine: { show: false },
  // Names sit right up against the bars without this.
  axisLabel: { ...categorySpec(theme, frame, { maxLabel }).axisLabel, margin: 10 },
});

export const valueAxisY = (theme: Theme, opts: { name?: string } = {}): YAXisComponentOption =>
  valueSpec(theme, opts);

export const valueAxisX = (theme: Theme, opts: { name?: string } = {}): XAXisComponentOption => ({
  ...valueSpec(theme, opts),
  // A value axis running horizontally puts its gridlines vertically, which is
  // the right read for bars: the eye follows a bar to the line it crosses.
  splitLine: { show: true, lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
});

/**
 * A legend is present whenever there are two or more series — identity is never
 * left to colour-matching alone. One series gets none: the chart's title
 * already names what is plotted, and a box with a single swatch just repeats it.
 */
export const legendFor = (
  theme: Theme,
  seriesCount: number,
  /**
   * The key mirrors the mark: a swatch for bars and areas, the series' own
   * line-and-symbol for lines. ECharts has no 'line' icon name — passing one
   * silently draws nothing — so lines simply leave `icon` unset and get the
   * default, which is already a line key.
   */
  mark: 'swatch' | 'line' = 'swatch',
): EChartsOption['legend'] =>
  seriesCount < 2
    ? undefined
    : {
        bottom: 0,
        left: 'center',
        ...(mark === 'swatch'
          ? { icon: 'roundRect' as const, itemWidth: 9, itemHeight: 9 }
          : { itemWidth: 18, itemHeight: 10 }),
        itemGap: 20,
        textStyle: { color: theme.inkSecondary, fontSize: 12, fontFamily: theme.font },
        inactiveColor: theme.inkMuted,
      };

/* -------------------------------------------------------------------------- */
/* Tooltips                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Tooltip content is built as DOM, not as an HTML string.
 *
 * Series and category names arrive from the upstream API — untrusted text as
 * far as this app is concerned. Concatenating them into `innerHTML`, which is
 * the usual ECharts formatter idiom, would execute whatever a column name
 * happened to contain. `textContent` cannot.
 */
const tooltipRow = ({
  color,
  name,
  value,
}: {
  color?: string;
  name: string;
  value: string;
}): HTMLElement => {
  const row = document.createElement('div');
  row.className = 'chart-tip__row';

  if (color) {
    const key = document.createElement('span');
    key.className = 'chart-tip__key';
    key.style.background = color;
    row.append(key);
  }

  const label = document.createElement('span');
  label.className = 'chart-tip__name';
  label.textContent = name;

  // Values lead, names follow: the reader already knows the series and came
  // for the number, so the number is the high-contrast element.
  const figure = document.createElement('strong');
  figure.className = 'chart-tip__value';
  figure.textContent = value;

  row.append(label, figure);
  return row;
};

const tooltipShell = (title: string, rows: HTMLElement[]): HTMLElement => {
  const root = document.createElement('div');
  root.className = 'chart-tip';

  if (title) {
    const heading = document.createElement('div');
    heading.className = 'chart-tip__title';
    heading.textContent = title;
    root.append(heading);
  }

  root.append(...rows);
  return root;
};

export const tooltipChrome = (theme: Theme) => ({
  backgroundColor: theme.surface,
  borderColor: theme.border,
  borderWidth: 1,
  padding: 0,
  extraCssText: `box-shadow: ${theme.shadowPop}; border-radius: 8px;`,
  textStyle: { color: theme.ink, fontSize: 12, fontFamily: theme.font },
});

interface AxisParam {
  axisValueLabel?: string;
  axisValue?: string;
  seriesName?: string;
  color?: string;
  value?: unknown;
  data?: unknown;
}

/**
 * Crosshair tooltip for line and column charts: a hairline tracks the pointer,
 * snaps to the nearest category, and the readout lists EVERY series at that x.
 * The reader aims at a date, never at a 2px stroke.
 */
export const axisTooltip = (theme: Theme, frame: Frame): EChartsOption['tooltip'] => ({
  trigger: 'axis',
  axisPointer: {
    type: 'line',
    lineStyle: { color: theme.axis, width: 1, type: 'solid' },
    label: { show: false },
  },
  ...tooltipChrome(theme),
  formatter: (params: unknown) => {
    const list = (Array.isArray(params) ? params : [params]) as AxisParam[];
    if (!list.length) return document.createElement('div');

    const raw = list[0].axisValueLabel ?? list[0].axisValue ?? '';
    const title = frame.temporal ? shortDate(String(raw)) : String(raw);

    const rows = list
      .filter((p) => p.value !== null && p.value !== undefined)
      .map((p) =>
        tooltipRow({
          color: p.color,
          name: p.seriesName ?? '',
          value: cell(p.value, 'exact'),
        }),
      );

    return tooltipShell(title, rows);
  },
});

/** Per-mark tooltip for bars, slices and points — the mark is the hit target. */
export const itemTooltip = (
  theme: Theme,
  opts: { showPercent?: boolean } = {},
): EChartsOption['tooltip'] => ({
  trigger: 'item',
  ...tooltipChrome(theme),
  formatter: (params: unknown) => {
    const p = params as AxisParam & { name?: string; percent?: number };
    const value = cell(typeof p.value === 'object' ? (p.data as number) : p.value, 'exact');
    const suffix = opts.showPercent && p.percent !== undefined ? ` · ${p.percent}%` : '';

    return tooltipShell(String(p.name ?? ''), [
      tooltipRow({
        color: p.color,
        name: p.seriesName ?? '',
        value: `${value}${suffix}`,
      }),
    ]);
  },
});

/* -------------------------------------------------------------------------- */
/* Mark styling                                                                */
/* -------------------------------------------------------------------------- */

/** Rounded at the data end, square at the baseline, so growth reads directionally. */
export const barRadius = (
  orientation: 'horizontal' | 'vertical',
  stacked: boolean,
): number | number[] => {
  // A stacked segment has no free end of its own — rounding every segment would
  // read as separate bars rather than parts of one.
  if (stacked) return 0;
  return orientation === 'vertical' ? [4, 4, 0, 0] : [0, 4, 4, 0];
};

/**
 * The 2px surface gap between touching marks. Stacked segments are the only
 * place marks actually touch, so it applies there — drawn in the surface colour
 * so it reads as space, not as an outline around the mark.
 */
export const stackGap = (theme: Theme) => ({
  borderColor: theme.surface,
  borderWidth: SURFACE_GAP / 2,
});

export const lineStyle = (color: string) => ({
  width: 2,
  color,
  cap: 'round' as const,
  join: 'round' as const,
});

/** Markers carry a ring in the surface colour so they stay legible when they
 *  cross a line or overlap each other. The ring is part of the hit target. */
export const markerStyle = (theme: Theme, color: string) => ({
  color,
  borderColor: theme.surface,
  borderWidth: SURFACE_GAP,
});

export const emphasisFocus = { focus: 'series' as const };

/** A subdued animation — enough to show the chart responding, never a show. */
export const animation = {
  animationDuration: 420,
  animationEasing: 'cubicOut' as const,
  animationDurationUpdate: 260,
};

/**
 * A direct label riding the last point of a line.
 *
 * Direct labels are sparing by design — the endpoint, the extreme, or the one
 * series the chart is about, never a number on every point. This uses ECharts'
 * series-level `endLabel` rather than a per-point `label` with a formatter that
 * blanks all but one index: a line drawn with `showSymbol: false` renders no
 * point labels at all, so the per-point approach silently produces nothing.
 */
export const lineEndLabel = (theme: Theme) => ({
  show: true,
  distance: 6,
  color: theme.inkSecondary,
  fontSize: 11,
  fontFamily: theme.font,
  // ECharts hands the formatter a wide `CallbackDataParams`; typing the
  // parameter loosely keeps this assignable where the option's generics differ.
  formatter: (p: { value?: unknown }) =>
    Array.isArray(p.value) ? cell(p.value[1]) : cell(p.value),
});

export const valueLabel = (theme: Theme, position: 'right' | 'top') => ({
  show: true,
  position,
  distance: 6,
  color: theme.inkSecondary,
  fontSize: 11,
  fontFamily: theme.font,
  formatter: (p: { value?: unknown }) =>
    p.value === null || p.value === undefined ? '' : cell(p.value),
});
